/**
 * ClientDetailById — thin redirect shim.
 *
 * The standalone canonical page was retired: every clinical surface
 * now lives on the workspace at ``/clients/<email>?as=<cid>``. This
 * component exists only so that:
 *
 *   * Legacy bookmarks / links to ``/clients/id/<cid>`` keep working
 *   * The global search + Split-created new sibling can hand off to
 *     the workspace with a single ``navigate()`` call
 *
 * We resolve the canonical row's email server-side (or synthesise a
 * routing-only placeholder when the row has no email at all) and
 * replace the URL so the redirect is invisible in the history stack.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, AlertTriangle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { workspacePath } from "@/lib/clientRoutes";

export default function ClientDetailById() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/clients/id/${encodeURIComponent(clientId)}`);
        if (cancelled) return;
        navigate(workspacePath(r.data), { replace: true });
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || "Client not found");
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, navigate]);

  if (error) {
    return (
      <div className="p-8" data-testid="client-by-id-missing">
        <div className="text-sm text-red-800 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Client not found — {error}.
        </div>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/clients")}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="p-8 text-sm text-zinc-500 flex items-center gap-2"
         data-testid="client-by-id-redirecting">
      <Loader2 className="h-4 w-4 animate-spin" /> Opening client workspace…
    </div>
  );
}
